import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, ChevronDown, CircleSlash, Search } from 'lucide-react';
import { INDUSTRY_OPTIONS } from '@/config/industries';
import { GROUP_ICONS, industryIcon } from '@/pages/industry-configs/industry-meta';

export interface TemplateInfo { fields: number }

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Field count per industry key, from Industry Configs. */
  templates?: Record<string, TemplateInfo>;
  /** Offer a "no industry" row (company create). */
  allowNone?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Industries not to offer (e.g. the ones a blueprint family already covers). */
  exclude?: string[];
  className?: string;
}

type Opt = (typeof INDUSTRY_OPTIONS)[number];

/**
 * Searchable, grouped industry picker for the super-admin. Each row shows how
 * many default fields that industry's template will copy into the company, so
 * the operator sees the consequence of the pick before making it.
 */
export function IndustryPicker({
  value, onChange, templates, allowNone, placeholder = 'Choose an industry', disabled, className, exclude,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const match = (o: Opt) => !term || o.label.toLowerCase().includes(term) || o.group.toLowerCase().includes(term) || o.value.includes(term);
    const out: { group: string; options: Opt[] }[] = [];
    for (const o of INDUSTRY_OPTIONS) {
      if (!match(o) || exclude?.includes(o.value)) continue;
      let g = out.find((x) => x.group === o.group);
      if (!g) { g = { group: o.group, options: [] }; out.push(g); }
      g.options.push(o);
    }
    return out;
  }, [q, exclude]);
  const showNone = allowNone && !q.trim();
  // Keyboard order = render order: optional "none" row, then grouped options.
  const flat = useMemo(
    () => [...(showNone ? [''] : []), ...groups.flatMap((g) => g.options.map((o) => o.value as string))],
    [groups, showNone],
  );

  useEffect(() => { setHighlight(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  const pick = (v: string) => { onChange(v); setOpen(false); setQ(''); };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => {
        const next = e.key === 'ArrowDown' ? Math.min(h + 1, flat.length - 1) : Math.max(h - 1, 0);
        listRef.current?.querySelector(`[data-idx="${next}"]`)?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    }
    if (e.key === 'Enter' && flat[highlight] !== undefined) { e.preventDefault(); pick(flat[highlight]); }
  };

  const current = INDUSTRY_OPTIONS.find((o) => o.value === value);
  const CurrentIcon = current ? industryIcon(current.value, current.group) : CircleSlash;
  const fieldsOf = (key: string) => templates?.[key]?.fields ?? 0;
  let idx = showNone ? 0 : -1;

  return (
    <div className={clsx('relative', className)} ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          'flex w-full items-center gap-3 rounded-xl border bg-surface px-3 py-2.5 text-left transition-colors',
          'hover:border-border-strong focus:outline-none focus:ring-4 focus:ring-primary-soft',
          open ? 'border-primary' : 'border-border',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className={clsx(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
          current ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted',
        )}>
          <CurrentIcon size={18} strokeWidth={1.8} />
        </span>
        <span className="min-w-0 flex-1">
          {current ? (
            <>
              <span className="block truncate text-sm font-semibold text-text-primary">{current.label}</span>
              <span className="block truncate text-2xs text-text-muted">
                {current.group}
                {templates && <> · {fieldsOf(current.value) > 0 ? `${fieldsOf(current.value)} default fields` : 'no default fields yet'}</>}
              </span>
            </>
          ) : (
            <span className="block text-sm text-text-muted">{placeholder}</span>
          )}
        </span>
        <ChevronDown size={15} className={clsx('shrink-0 text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className="fade-up absolute left-0 right-0 z-40 mt-1.5 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-elevated"
          style={{ animationDuration: '180ms' }}
          onKeyDown={onKeyDown}
        >
          <div className="relative border-b border-border p-2">
            <Search size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 54 industries — bakery, software, clinic…"
              className="h-9 w-full rounded-lg border border-border bg-surface pl-8 pr-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft"
            />
          </div>

          <div ref={listRef} className="max-h-80 overflow-y-auto pb-1" role="listbox">
            {showNone && (
              <button
                type="button"
                role="option"
                aria-selected={value === ''}
                data-idx={0}
                onMouseEnter={() => setHighlight(0)}
                onClick={() => pick('')}
                className={clsx('flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm', highlight === 0 ? 'bg-surface-alt' : 'hover:bg-surface-alt')}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-surface-alt text-text-muted"><CircleSlash size={14} /></span>
                <span className="flex-1 text-text-secondary">No industry — let the company pick at onboarding</span>
                {value === '' && <Check size={14} className="text-primary" />}
              </button>
            )}
            {groups.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-text-muted">No industry matches “{q}”.</p>
            ) : groups.map((g) => {
              const GroupIcon = GROUP_ICONS[g.group];
              return (
                <div key={g.group}>
                  <div className="sticky top-0 z-10 flex items-center gap-1.5 bg-surface-elevated/95 px-3 pb-1 pt-2.5 text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted backdrop-blur">
                    {GroupIcon && <GroupIcon size={11} />} {g.group}
                  </div>
                  {g.options.map((o) => {
                    idx += 1;
                    const i = idx;
                    const Icon = industryIcon(o.value, o.group);
                    const selected = o.value === value;
                    const n = fieldsOf(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        data-idx={i}
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => pick(o.value)}
                        className={clsx(
                          'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                          i === highlight ? 'bg-surface-alt' : 'hover:bg-surface-alt',
                        )}
                      >
                        <span className={clsx(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                          selected ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-secondary',
                        )}>
                          <Icon size={14} strokeWidth={1.8} />
                        </span>
                        <span className={clsx('min-w-0 flex-1 truncate text-text-primary', selected && 'font-semibold')}>{o.label}</span>
                        {templates && (
                          <span className={clsx(
                            'shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                            n > 0 ? 'bg-primary-soft text-primary' : 'text-text-muted',
                          )}>
                            {n > 0 ? `${n} fields` : '—'}
                          </span>
                        )}
                        {selected && <Check size={14} className="shrink-0 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
